/**
 * BriefingSettingsModal — Zahnrad am Morgen-Briefing (Jul-2026, User-Wunsch):
 * zeigt alle Informations-Bausteine des Briefings und ab welcher Stufe sie
 * sichtbar sind (Aus / ab Mitarbeiter / ab Filialleiter / nur Führung).
 * Gilt betriebsweit (GET/PUT /assistant/briefing-settings).
 */
import { useEffect, useState } from "react";
import { X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";

const LEVELS = [
    { v: 0, l: "Aus" },
    { v: 1, l: "ab Mitarbeiter" },
    { v: 2, l: "ab Filialleiter" },
    { v: 3, l: "nur Führung" },
];

export default function BriefingSettingsModal({ onClose }) {
    const [cats, setCats] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/assistant/briefing-settings")
            .then((r) => setCats(r.data.categories || []))
            .catch(() => { toast.error("Einstellungen konnten nicht geladen werden"); onClose?.(); });
    }, [onClose]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = Object.fromEntries((cats || []).map((c) => [c.key, c.level]));
            await api.put("/assistant/briefing-settings", { categories: payload });
            toast.success("Briefing-Einstellungen gespeichert");
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="briefing-settings-modal">
            <div className="w-full max-w-lg mt-12 bg-white border border-gray-200 rounded-sm shadow-xl text-[#111827]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black">Briefing-Bausteine</h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" data-testid="briefing-settings-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5">
                    <p className="text-[11px] text-gray-500 mb-3">
                        Diese Informationen werden im Morgen-Briefing verarbeitet. Je Baustein legst du fest,
                        ab welcher Stufe er erscheint: Mitarbeiter sehen nur eigene Arbeit, Filialleiter ihren
                        Standort, Führung (Inhaber/GF/Gebietsleiter) die gesamte Organisation. Gilt betriebsweit.
                    </p>
                    {cats === null ? (
                        <div className="py-8 text-center text-gray-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {cats.map((c, i) => (
                                <li key={c.key} className="py-2 flex items-center gap-3" data-testid={`briefing-setting-${c.key}`}>
                                    <span className="text-sm font-bold flex-1">{c.label}</span>
                                    <select value={c.level}
                                        onChange={(e) => setCats((p) => p.map((x, xi) => xi === i ? { ...x, level: Number(e.target.value) } : x))}
                                        className="h-8 text-xs rounded-sm border border-gray-300 px-2 bg-white"
                                        data-testid={`briefing-setting-level-${c.key}`}>
                                        {LEVELS.map((lv) => <option key={lv.v} value={lv.v}>{lv.l}</option>)}
                                    </select>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-sm">
                    <Button type="button" variant="outline" onClick={onClose} className="h-9 rounded-sm text-sm" data-testid="briefing-settings-cancel">Abbrechen</Button>
                    <Button type="button" onClick={save} disabled={saving || cats === null}
                        className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="briefing-settings-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Speichern
                    </Button>
                </div>
            </div>
        </div>
    );
}
